import {
  ForwardRefRenderFunction,
  PropsWithChildren,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { useClickOutside } from 'hooks/browser/outerClick';
import { useToggleUI } from 'hooks/pages/companySlug/create.hooks';

interface IProps {
  onClose?: () => void;
}

interface IDrawerHandle {
  openDrawer: () => void;
  closeDrawer: () => void;
}

const InternalDrawer: ForwardRefRenderFunction<
  IDrawerHandle,
  PropsWithChildren<IProps>
> = ({ children, onClose }, ref) => {
  const {
    isActive: isDrawerOpen,
    setActive: openDrawer,
    setInactive: closeDrawer,
  } = useToggleUI();

  const contentRef = useRef<HTMLDivElement>(null);
  // The listener stays attached while the drawer is closed, so without this
  // guard every click on the page would run `onClose`.
  useClickOutside(contentRef, () => {
    if (!isDrawerOpen) {
      return;
    }

    if (typeof onClose === 'function') {
      onClose();
    } else {
      closeDrawer();
    }
  });

  useImperativeHandle(ref, () => ({
    openDrawer,
    closeDrawer,
  }));

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <motion.aside
          className="fixed inset-0 z-[1000000000] flex flex-row items-stretch justify-end bg-black/60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-[90%] sm:max-w-[500px]"
            initial={{ translateX: '100%' }}
            animate={{ translateX: 0 }}
            exit={{ translateX: '100%' }}
            transition={{ bounce: 0 }}
          >
            <div
              ref={contentRef}
              className="flex h-full w-full flex-col justify-start overflow-auto bg-alabasterGrey dark:bg-darkMineShaftGrey"
            >
              {children}
            </div>
          </motion.div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

InternalDrawer.displayName = 'Drawer';

export const Drawer = forwardRef(InternalDrawer);
